import type { Chain, DynamicClient } from '@dynamic-labs-sdk/client';
import {
  type WalletAccount,
  verifyWalletAccount,
} from '@dynamic-labs-sdk/client';
import type { AuthTypes } from '@walletconnect/types';

import type { WalletProviderBuilder } from '../walletConnectWalletProvider.types';
import { importWalletConnectSession } from '../importWalletConnectSession';
import { verifyWalletConnectAuths } from './verifyWalletConnectAuths';

type ImportWalletConnectAuthResponseParams = {
  /**
   * The response from the WalletConnect authenticate method.
   */
  authResponse: AuthTypes.AuthenticateResponseResult;

  /**
   * The chain with which the wallet account is associated.
   */
  chain: Chain;

  /**
   * The function that builds the wallet provider for the session.
   */
  walletProviderBuilder: WalletProviderBuilder;
};

/**
 * A low level function that is used to connect a WalletConnect authenticate response to the Dynamic SDK.
 * It will generate the wallet providers and wallet accounts for all accounts in the session.
 *
 * @see WalletConnect's docs on [session authentication](https://docs.reown.com/advanced/api/sign/dapp-usage#session-authenticate-with-recaps)
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the connected and verified wallet account.
 */
export const importWalletConnectAuthResponse = async (
  {
    authResponse,
    chain,
    walletProviderBuilder,
  }: ImportWalletConnectAuthResponseParams,
  client: DynamicClient
): Promise<{ walletAccounts: WalletAccount[] }> => {
  const { session, auths } = authResponse;

  /**
   * Some wallets do not support WC's authenticate method (such as Rainbow).
   * For these wallets, the authenticate method will act as a regular connect method.
   * Therefore we must call verify individually.
   */
  if (!auths || auths.length === 0) {
    const { walletAccounts } = await importWalletConnectSession(
      {
        /**
         * Don't add the wallet accounts to the dynamic's wallet accounts while we are still verifying them.
         */
        addToDynamicWalletAccounts: false,
        sessionTopic: session.topic,
        walletProviderBuilder,
      },
      client
    );

    // This should not be concurrent because the first will be a verify and the rest will be a link
    for (const walletAccount of walletAccounts) {
      await verifyWalletAccount({ walletAccount }, client);
    }

    return { walletAccounts };
  }

  /**
   * This reconstructs the message that was signed by the wallet and verifies the signature
   * in the backend. It will update the user with the new verified credentials.
   */
  await verifyWalletConnectAuths(
    {
      auths,
      chain,
      walletDisplayName: session.peer.metadata.name,
    },
    client
  );

  const { walletAccounts } = await importWalletConnectSession(
    {
      addToDynamicWalletAccounts: true,
      sessionTopic: session.topic,
      walletProviderBuilder,
    },
    client
  );

  return { walletAccounts };
};
